import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, TrendingUp, Users, Sparkles, Activity, Lightbulb } from 'lucide-react';

const Home = () => {
  // Update page title
  React.useEffect(() => {
    document.title = "Home - Kodamalas Krushi Foundation | Complete Child Development";
  }, []);
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  // Advanced animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        damping: 80,
        stiffness: 400
      }
    }
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [-5, 5, -5],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Solutions",
      description: "Leveraging cutting-edge artificial intelligence to solve complex societal challenges with precision and scale."
    },
    {
      icon: TrendingUp,
      title: "Physical Growth Monitoring",
      description: "BoneAH uses AI to identify physical growth issues in children, enabling early intervention and better health outcomes."
    },
    {
      icon: Users,
      title: "Mental Development",
      description: "IQplayground application helps assess and enhance mental growth through engaging, technology-driven activities."
    },
    {
      icon: Sparkles,
      title: "Emotional Support",
      description: "Speak to the God provides spiritual guidance and emotional wellbeing support, helping children find inner peace and resilience."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Dark Theme with Advanced Effects */}
      <section className="relative pt-32 pb-20 overflow-hidden gradient-hero">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-96 h-96 bg-[var(--brand-primary)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-[var(--brand-purple)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute -bottom-20 left-1/2 w-96 h-96 bg-[var(--brand-coral)] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-float" style={{animationDelay: '4s'}}></div>
        </div>
        
        <div className="container mx-auto px-6 md:px-8 lg:px-12 relative z-10">
          <div className="text-center mb-12 max-w-5xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.1 }}
              className="display-hero text-[var(--text-primary)] mb-6"
            >
              Complete Child Development<br/>Through Technology
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="body-large text-[var(--text-secondary)] mb-8"
            >
              Holistic growth through three essential pillars—Physical Health, Mental Development, and Emotional Wellbeing
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/services">
                <button className="btn-primary flex items-center gap-2">
                  Discover Our Solutions
                  <ArrowRight size={18} />
                </button>
              </Link>
              <Link to="/contact#contact-form">
                <button className="btn-secondary">
                  Get in Touch
                </button>
              </Link>
            </motion.div>
          </div>

          {/* Central Image with Three Pillars Around */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.4 }}
            className="max-w-6xl mx-auto"
          >
            {/* Top Row - Image with Advanced Effects */}
            <div className="flex justify-center mb-8">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-md animate-pulse-glow">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-foundation-6/artifacts/62s2akyt_futuristic-portrait-young-girl-with-high-tech.jpg" 
                  alt="Child with technology"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-primary)] via-transparent to-transparent"></div>
                
                {/* Floating badge with glass morphism */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass-card rounded-2xl p-4 border border-[var(--border-light)]">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse-glow">
                        <Sparkles size={24} className="text-[var(--bg-primary)]" />
                      </div>
                      <div>
                        <div className="text-lg font-bold text-[var(--text-primary)]">AI-Powered</div>
                        <p className="text-sm text-[var(--text-secondary)]">Technology for Every Child</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - Three Pillars */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Physical Health Panel */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springConfig, delay: 0.5 }}
                className="glass-card rounded-3xl p-8 shadow-lg hover-lift relative overflow-hidden border-t-4 border-[var(--brand-primary)]"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--brand-primary)] opacity-20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="absolute inset-0 shimmer"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-primary)]/30">
                    <Activity size={32} className="text-[var(--bg-primary)]" />
                  </div>
                  <h3 className="heading-large text-[var(--text-primary)] mb-4 neon-green">
                    Physical<br/>Health
                  </h3>
                  <p className="body-standard text-[var(--text-secondary)] mb-6">
                    AI-powered bone age assessment and growth monitoring through BoneAH technology
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-primary)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Early detection & intervention
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-primary)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Non-invasive growth monitoring
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-primary)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Accurate AI-powered results
                    </div>
                  </div>
                  <a href="https://boneah.com/" target="_blank" rel="noopener noreferrer">
                    <button className="body-standard text-[var(--brand-primary)] font-medium flex items-center gap-2 hover:gap-4 transition-all hover:text-[var(--brand-secondary)]">
                      Learn More <ArrowRight size={16} />
                    </button>
                  </a>
                </div>
              </motion.div>

              {/* Mental Health Panel */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springConfig, delay: 0.6 }}
                className="glass-card rounded-3xl p-8 shadow-lg hover-lift relative overflow-hidden border-t-4 border-[var(--brand-purple)]"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--brand-purple)] opacity-20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="absolute inset-0 shimmer"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-purple)] to-[#8B5CF6] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-purple)]/30">
                    <Brain size={32} className="text-white" />
                  </div>
                  <h3 className="heading-large text-[var(--text-primary)] mb-4 neon-purple">
                    Mental<br/>Development
                  </h3>
                  <p className="body-standard text-[var(--text-secondary)] mb-6">
                    Cognitive development and assessment through IQplayground platform
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-purple)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Interactive cognitive assessments
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-purple)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Real-time progress tracking
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-purple)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Engaging learning activities
                    </div>
                  </div>
                  <Link to="/services">
                    <button className="body-standard text-[var(--brand-purple)] font-medium flex items-center gap-2 hover:gap-4 transition-all hover:brightness-110">
                      Learn More <ArrowRight size={16} />
                    </button>
                  </Link>
                </div>
              </motion.div>

              {/* Emotional Wellbeing Panel */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springConfig, delay: 0.7 }}
                className="glass-card rounded-3xl p-8 shadow-lg hover-lift relative overflow-hidden border-t-4 border-[var(--brand-coral)]"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[var(--brand-coral)] opacity-20 rounded-full -mr-16 -mt-16 blur-2xl"></div>
                <div className="absolute inset-0 shimmer"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-coral)] to-[#FF4D7D] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-coral)]/30">
                    <Sparkles size={32} className="text-white" />
                  </div>
                  <h3 className="heading-large text-[var(--text-primary)] mb-4 neon-coral">
                    Emotional<br/>Wellbeing
                  </h3>
                  <p className="body-standard text-[var(--text-secondary)] mb-6">
                    Emotional support and spiritual guidance through Speak to the God application
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-coral)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Spiritual guidance & emotional support
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-coral)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Safe space for expression
                    </div>
                    <div className="flex items-center gap-3 text-sm text-[var(--text-secondary)] group">
                      <div className="w-2 h-2 bg-[var(--brand-coral)] rounded-full group-hover:scale-125 transition-transform"></div>
                      Building resilience & inner peace
                    </div>
                  </div>
                  <button className="body-standard text-[var(--brand-coral)] font-medium flex items-center gap-2 opacity-60 cursor-not-allowed">
                    Coming Soon <Sparkles size={16} />
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Bharat's Bala Vardhan Mission Highlight */}
      <section className="py-16 px-6 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={springConfig}
            className="glass-card rounded-3xl border border-[var(--brand-primary)]/30 overflow-hidden relative"
            data-testid="bala-vardhan-highlight"
          >
            <div className="absolute inset-0 morphing-bg"></div>
            <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--brand-primary)] opacity-10 rounded-full -mr-24 -mt-24 blur-3xl"></div>
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center p-8 md:p-12">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card border border-[var(--brand-primary)]/40 mb-5">
                  <span className="w-2 h-2 rounded-full bg-[var(--brand-primary)] animate-pulse"></span>
                  <span className="body-small text-[var(--brand-primary)] font-medium">New Initiative</span>
                </div>
                <h2 className="heading-large text-[var(--text-primary)] mb-3">
                  Bharat's Bala Vardhan Mission
                </h2>
                <p className="body-large text-[var(--text-secondary)] mb-2">
                  India's Child Growth &amp; Strengthening Mission — powered by Tots360, AI Powered Child Care.
                </p>
                <p className="body-standard text-[var(--brand-primary)] font-medium mb-6">
                  Know Every Child. Grow Every Child. Strengthen Every Generation.
                </p>
                <Link to="/bala-vardhan-mission">
                  <button className="btn-primary flex items-center gap-2" data-testid="bala-vardhan-learn-more-btn">
                    Learn More
                    <ArrowRight size={18} />
                  </button>
                </Link>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[var(--border-light)] bg-white aspect-[4/3] flex items-center justify-center p-4">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_bbc9e96f-7875-4deb-b7db-7c2f321eb51d/artifacts/m8a302nx_Bharat%27s%20Bala%20Vardhan%20Mission1.png"
                  alt="Bharat's Bala Vardhan Mission logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Three Pillars Explanation Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-primary)]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              The Three Pillars of Growth
            </h2>
            <p className="body-large text-[var(--text-secondary)] max-w-4xl mx-auto">
              Complete child development requires nurturing physical health, mental development, and emotional wellbeing. Our comprehensive approach ensures every aspect is supported.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.1 }}
              className="glass-card rounded-2xl overflow-hidden border border-[var(--border-light)] hover-lift"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-foundation-6/artifacts/i0zbrhgu_3d-cartoon-fitness-man%20%282%29.jpg" 
                  alt="Physical fitness and health"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center shadow-lg shadow-[var(--brand-primary)]/50">
                  <Activity size={32} className="text-[var(--bg-primary)]" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="heading-medium text-[var(--text-primary)] mb-3">Physical Foundation</h3>
                <p className="body-standard text-[var(--text-secondary)]">
                  Early detection of physical growth issues through AI ensures healthy bodily development and timely medical intervention
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="glass-card rounded-2xl overflow-hidden border border-[var(--border-light)] hover-lift"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-foundation-6/artifacts/dk8ptl5y_3d-cartoon-fitness-man.jpg" 
                  alt="Mental development and learning"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-[var(--brand-purple)] to-[#8B5CF6] rounded-2xl flex items-center justify-center shadow-lg shadow-[var(--brand-purple)]/50">
                  <Brain size={32} className="text-white" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="heading-medium text-[var(--text-primary)] mb-3">Mental Excellence</h3>
                <p className="body-standard text-[var(--text-secondary)]">
                  Cognitive assessments and engaging activities foster critical thinking, problem-solving, and intellectual growth
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.3 }}
              className="glass-card rounded-2xl overflow-hidden border border-[var(--border-light)] hover-lift"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_tech-foundation-6/artifacts/xt8t0p0o_happy-vesak-day-illustration.jpg" 
                  alt="Emotional wellbeing and spiritual peace"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] to-transparent"></div>
                <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-[var(--brand-coral)] to-[#FF4D7D] rounded-2xl flex items-center justify-center shadow-lg shadow-[var(--brand-coral)]/50">
                  <Sparkles size={32} className="text-white" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="heading-medium text-[var(--text-primary)] mb-3">Emotional Balance</h3>
                <p className="body-standard text-[var(--text-secondary)]">
                  Spiritual guidance and emotional support help children develop resilience, inner peace, and emotional intelligence
                </p>
              </div>
            </motion.div>
          </div>

          {/* Central Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.4 }}
            className="glass-card rounded-3xl p-10 text-center border border-[var(--border-light)]"
          >
            <h3 className="heading-medium text-[var(--text-primary)] mb-4">
              Together, They Create Complete Wellness
            </h3>
            <p className="body-large text-[var(--text-secondary)] max-w-4xl mx-auto">
              When physical health, mental development, and emotional wellbeing work in harmony, children can reach their full potential and thrive in all aspects of life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Holistic Child Development Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              Complete Child Development
            </h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              A child's overall growth depends on nurturing physical health, mental development, and emotional wellbeing. Our AI-powered solutions provide comprehensive support for every aspect of development.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Physical Health Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-3xl hover-lift border-2 border-[var(--brand-primary)]/30"
            >
              <div className="p-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-primary)]/50">
                <TrendingUp size={32} className="text-[var(--bg-primary)]" />
              </div>
              <h3 className="heading-large text-[var(--text-primary)] mb-4">
                Physical Health
              </h3>
              <p className="body-large text-[var(--text-secondary)] mb-6">
                Early detection of physical growth issues is crucial for healthy development. BoneAH uses AI to identify potential concerns before they become serious problems.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">AI-powered bone age assessment</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Growth disorder detection</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-primary)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Non-invasive monitoring</p>
                </div>
              </div>
                <a href="https://boneah.com/" target="_blank" rel="noopener noreferrer">
                  <button className="btn-primary w-full">
                    Learn About BoneAH
                  </button>
                </a>
              </div>
            </motion.div>

            {/* Mental Health Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.1 }}
              className="glass-card rounded-3xl hover-lift border-2 border-[var(--brand-purple)]/30"
            >
              <div className="p-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-purple)] to-[#8B5CF6] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-purple)]/50">
                <Brain size={32} className="text-white" />
              </div>
              <h3 className="heading-large text-[var(--text-primary)] mb-4">
                Mental Health
              </h3>
              <p className="body-large text-[var(--text-secondary)] mb-6">
                Cognitive and emotional development are equally vital. IQplayground provides engaging activities that assess and enhance mental growth in children.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#8B5CF6] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Interactive cognitive assessments</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#8B5CF6] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Mental development tracking</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[#8B5CF6] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Engaging learning activities</p>
                </div>
              </div>
                <Link to="/services">
                  <button className="btn-primary w-full" style={{background: 'linear-gradient(135deg, var(--brand-purple) 0%, #8B5CF6 100%)'}}>
                    Discover IQplayground
                  </button>
                </Link>
              </div>
            </motion.div>

            {/* Emotional Wellbeing Card */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.2 }}
              className="glass-card rounded-3xl hover-lift border-2 border-[var(--brand-coral)]/30"
            >
              <div className="p-8">
              <div className="w-16 h-16 bg-gradient-to-br from-[var(--brand-coral)] to-[#FF4D7D] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[var(--brand-coral)]/50">
                <Sparkles size={32} className="text-white" />
              </div>
              <h3 className="heading-large text-[var(--text-primary)] mb-4">
                Emotional Wellbeing
              </h3>
              <p className="body-large text-[var(--text-secondary)] mb-6">
                Emotional support and spiritual guidance are essential. Speak to the God helps children develop resilience, inner peace, and emotional intelligence.
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-coral)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Spiritual guidance & emotional support</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-coral)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Safe space for expression</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-[var(--brand-coral)] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <p className="body-standard text-[var(--text-secondary)]">Building resilience & inner peace</p>
                </div>
              </div>
                <button className="btn-primary w-full opacity-60 cursor-not-allowed" style={{background: 'linear-gradient(135deg, var(--brand-coral) 0%, #FF4D7D 100%)'}}>
                  Coming Soon
                </button>
              </div>
            </motion.div>
          </div>

          {/* Central Message */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="mt-12 text-center glass-card rounded-3xl p-10 border border-[var(--border-light)]"
          >
            <h3 className="heading-medium text-[var(--text-primary)] mb-4">
              Together, They Build a Healthy Future
            </h3>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              By addressing both physical and mental health, we provide a complete picture of child development, ensuring every child has the opportunity to reach their full potential.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-light)]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              Our Purpose
            </h2>
            <p className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto">
              Kodamalas Krushi Foundation is dedicated to community development through innovative technology solutions
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-2xl p-8 hover-lift border border-[var(--border-light)]"
            >
              <h3 className="heading-medium text-[var(--brand-primary)] neon-green mb-4">Vision</h3>
              <p className="body-standard text-[var(--text-secondary)]">
                To build a better and inclusive world by harnessing the power of technology to solve real-world challenges and improve lives across communities.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
              className="glass-card rounded-2xl p-8 hover-lift border border-[var(--border-light)]"
            >
              <h3 className="heading-medium text-[var(--brand-primary)] neon-green mb-4">Mission</h3>
              <p className="body-standard text-[var(--text-secondary)]">
                To leverage technology to create sustainable, inclusive, and scalable solutions that address real-world problems and improve lives in society.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced Technology Approach Section with Advanced Animations */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-primary)] relative overflow-hidden">
        {/* Animated background particles */}
        <div className="absolute inset-0">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -30, 0],
                x: [0, 10, -10, 0],
                opacity: [0.1, 0.3, 0.1],
              }}
              transition={{
                duration: 4 + i,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
              }}
              className={`absolute w-2 h-2 bg-[var(--brand-primary)] rounded-full blur-sm`}
              style={{
                left: `${10 + i * 15}%`,
                top: `${20 + i * 10}%`,
              }}
            />
          ))}
        </div>

        <div className="container mx-auto relative z-10">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="text-center mb-16"
          >
            <motion.h2
              variants={itemVariants}
              className="heading-large text-[var(--text-primary)] mb-4"
            >
              Our Technology-Driven Approach
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="body-large text-[var(--text-secondary)] max-w-3xl mx-auto"
            >
              Leveraging the latest in AI and technology to solve real-world challenges in child development
            </motion.p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.1,
                  rotateY: 10,
                  rotateX: 5,
                  transition: { duration: 0.3 }
                }}
                whileTap={{ scale: 0.95 }}
                className="glass-card rounded-2xl p-6 hover-lift border border-[var(--border-light)] group perspective-1000 relative overflow-hidden"
              >
                {/* Hover glow effect */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  whileHover={{ 
                    opacity: 0.2, 
                    scale: 1,
                    transition: { duration: 0.3 }
                  }}
                  className="absolute inset-0 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-purple)] rounded-2xl blur-xl"
                />
                
                <div className="relative z-10">
                  <motion.div 
                    whileHover={{ 
                      rotate: [0, -15, 15, 0],
                      scale: [1, 1.2, 1],
                      transition: { duration: 0.6 }
                    }}
                    className="w-12 h-12 bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-secondary)] rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:shadow-[var(--brand-primary)]/50"
                  >
                    <feature.icon size={24} className="text-white" />
                  </motion.div>
                  <h3 className="heading-medium text-[var(--text-primary)] mb-3 group-hover:text-[var(--brand-primary)] transition-colors">
                    {feature.title}
                  </h3>
                  <p className="body-standard text-[var(--text-secondary)] group-hover:text-[var(--text-primary)] transition-colors">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-8 lg:px-12 bg-[var(--bg-secondary)]">
        <div className="container mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="glass-card rounded-3xl p-12 text-center max-w-4xl mx-auto border border-[var(--border-light)]"
          >
            <h2 className="heading-large text-[var(--text-primary)] mb-4">
              Join Us in Making a Difference
            </h2>
            <p className="body-large text-[var(--text-secondary)] mb-8 max-w-2xl mx-auto">
              Be part of our mission to leverage technology for social good. Together, we can create lasting impact in communities.
            </p>
            <Link to="/contact">
              <button className="btn-primary">
                Get Involved Today
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;